import { useCallback, useEffect, useState } from "react";
import { adminApi, API } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Upload, Eye, EyeOff } from "lucide-react";

const EMPTY_FORM = { title: "", subtitle: "", image: "", link: "", section: "home", position: 0, visible: true };

/**
 * AdminCustomTiles — dynamic homepage / shop / catalogue tiles CRUD.
 * Self-contained: fetches, creates, edits, deletes and toggles visibility of custom tiles.
 */
export default function AdminCustomTiles() {
  const [tiles, setTiles] = useState([]);
  const [editing, setEditing] = useState(null); // tile object or "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadBusy, setUploadBusy] = useState(false);

  const reload = useCallback(
    () => adminApi().get("/admin/custom-tiles").then((r) => setTiles(r.data || [])).catch(() => {}),
    [],
  );
  useEffect(() => { reload(); }, [reload]);

  const startNew = () => {
    setEditing("new");
    setForm({ ...EMPTY_FORM, position: tiles.length });
  };
  const startEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title || "",
      subtitle: t.subtitle || "",
      image: t.image || "",
      link: t.link || "",
      section: t.section || "home",
      position: t.position || 0,
      visible: t.visible !== false,
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); };

  const uploadImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${API}${up.data.url.replace(/^\/api/, "")}`;
      setForm((f) => ({ ...f, image: fullUrl }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title.trim(),
      subtitle: (form.subtitle || "").trim(),
      image: form.image.trim(),
      link: (form.link || "").trim() || "/",
      section: form.section || "home",
      position: Number(form.position) || 0,
      visible: !!form.visible,
    };
    if (!payload.title) { toast.error("Title is required"); return; }
    if (!payload.image) { toast.error("Image is required"); return; }
    try {
      if (editing === "new") {
        await adminApi().post("/admin/custom-tiles", payload);
        toast.success("Tile created");
      } else {
        await adminApi().put(`/admin/custom-tiles/${editing.id}`, payload);
        toast.success("Tile updated");
      }
      cancel();
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Move tile "${t.title}" to Recently Deleted?\n\nYou can restore it within 30 days from the Recently Deleted tab.`)) return;
    try {
      await adminApi().delete(`/admin/custom-tiles/${t.id}`);
      toast.success("Tile moved to Recently Deleted");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const toggleVisible = async (t) => {
    try {
      await adminApi().put(`/admin/custom-tiles/${t.id}`, { visible: !t.visible });
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  return (
    <>
      <div className="mt-6" data-testid="admin-custom-tiles-tab">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="text-sm text-[#5C5555]">Create fully-custom tiles for the homepage (Featured section), shop pages, or catalogues. Each tile has a title, subtitle, image, link and visibility toggle. Drag or edit position to reorder.</p>
            <p className="text-[11px] text-[#5C5555] mt-1">Deleting a tile moves it to Recently Deleted — restorable for 30 days.</p>
          </div>
          <button
            type="button"
            onClick={startNew}
            data-testid="tile-add-new"
            className="btn-primary text-xs sm:text-sm px-3 sm:px-5 py-2 whitespace-nowrap inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4"/> Add Tile
          </button>
        </div>

        {tiles.length === 0 ? (
          <p className="text-center text-[#5C5555] py-14 italic font-hand">No custom tiles yet. Click <b>Add Tile</b> above to create one 🌸</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {tiles.map((t) => (
              <div key={t.id} className={`bg-white border rounded-lg p-3 flex flex-col ${t.visible ? "border-[#E5DBD8]" : "border-dashed border-[#5C5555] opacity-70"}`} data-testid={`custom-tile-${t.id}`}>
                <div className="aspect-video overflow-hidden rounded bg-[#F2E8E4] mb-2">
                  {t.image ? <img src={t.image} alt={t.title} className="w-full h-full object-cover"/> : null}
                </div>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-serif text-base text-[#2D2424] leading-tight flex-1">{t.title}</p>
                  <span className="text-[10px] uppercase tracking-widest bg-[#FFE7F1] text-[#7A1F2B] px-2 py-0.5 rounded-full font-doodle whitespace-nowrap">{t.section}</span>
                </div>
                {t.subtitle && <p className="font-hand text-xs text-[#5C5555] mb-1">{t.subtitle}</p>}
                <p className="text-[11px] text-[#5C5555] mb-2 truncate">🔗 {t.link}</p>
                <div className="mt-auto flex items-center gap-2 flex-wrap pt-2">
                  <button
                    type="button"
                    onClick={() => toggleVisible(t)}
                    data-testid={`tile-visible-toggle-${t.id}`}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1 ${t.visible ? "bg-[#EAFBE7] text-[#2D5F1E] border border-[#2D5F1E]/30" : "bg-[#F2E8E4] text-[#5C5555] border border-[#E5DBD8]"}`}
                  >
                    {t.visible ? <Eye className="w-3 h-3"/> : <EyeOff className="w-3 h-3"/>}
                    {t.visible ? "Visible" : "Hidden"}
                  </button>
                  <span className="text-[10px] text-[#5C5555]">#{t.position}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <button type="button" onClick={() => startEdit(t)} data-testid={`tile-edit-${t.id}`} className="w-8 h-8 rounded-full border border-[#E5DBD8] text-[#7A1F2B] flex items-center justify-center hover:bg-[#FFE7F1]"><Edit2 className="w-4 h-4"/></button>
                    <button type="button" onClick={() => remove(t)} data-testid={`tile-delete-${t.id}`} className="w-8 h-8 rounded-full border border-[#E5DBD8] text-[#7A1F2B] flex items-center justify-center hover:bg-[#FFE7F1]"><Trash2 className="w-4 h-4"/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={cancel}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white p-4 sm:p-6 max-w-xl w-full max-h-[92vh] overflow-y-auto rounded-lg" data-testid="tile-edit-modal">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="font-serif text-xl sm:text-2xl">{editing === "new" ? "New Custom Tile" : "Edit Tile"}</h2>
              <button type="button" onClick={cancel} className="p-1 -mr-1"><X className="w-5 h-5"/></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <label className="sm:col-span-2">
                <span className="text-xs uppercase tracking-wider text-[#5C5555]">Title *</span>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="tile-form-title" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 rounded focus:outline-none focus:border-[#7A1F2B]" placeholder="e.g. Festive Corporate Hampers"/>
              </label>
              <label className="sm:col-span-2">
                <span className="text-xs uppercase tracking-wider text-[#5C5555]">Subtitle</span>
                <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="tile-form-subtitle" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 rounded focus:outline-none focus:border-[#7A1F2B]" placeholder="Short line under the title"/>
              </label>
              <label className="sm:col-span-2">
                <span className="text-xs uppercase tracking-wider text-[#5C5555]">Image *</span>
                <div className="mt-1 flex flex-col sm:flex-row gap-2 items-stretch">
                  <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="tile-form-image-url" className="flex-1 border border-[#E5DBD8] px-3 py-2 rounded focus:outline-none focus:border-[#7A1F2B]" placeholder="https://..."/>
                  <label className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#7A1F2B] text-white text-xs uppercase tracking-wider rounded cursor-pointer hover:bg-[#5C1520] whitespace-nowrap ${uploadBusy ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <Upload className="w-3.5 h-3.5"/> {uploadBusy ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/*" disabled={uploadBusy} className="hidden" data-testid="tile-form-image-upload" onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ""; }}/>
                  </label>
                </div>
                {form.image && (
                  <div className="mt-2 aspect-video max-w-xs overflow-hidden rounded border border-[#E5DBD8]">
                    <img src={form.image} alt="preview" className="w-full h-full object-cover"/>
                  </div>
                )}
              </label>
              <label className="sm:col-span-2">
                <span className="text-xs uppercase tracking-wider text-[#5C5555]">Link *</span>
                <input required value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="tile-form-link" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 rounded focus:outline-none focus:border-[#7A1F2B]" placeholder="/shop/her or https://..."/>
              </label>
              <label>
                <span className="text-xs uppercase tracking-wider text-[#5C5555]">Section</span>
                <select value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} data-testid="tile-form-section" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 rounded focus:outline-none focus:border-[#7A1F2B] bg-white">
                  <option value="home">Home (Featured row)</option>
                  <option value="shop">Shop pages</option>
                  <option value="catalogues">Catalogues page</option>
                </select>
              </label>
              <label>
                <span className="text-xs uppercase tracking-wider text-[#5C5555]">Position</span>
                <input type="number" value={form.position} onChange={(e) => setForm({ ...form, position: Number(e.target.value) })} data-testid="tile-form-position" className="mt-1 w-full border border-[#E5DBD8] px-3 py-2 rounded focus:outline-none focus:border-[#7A1F2B]"/>
              </label>
              <label className="sm:col-span-2 inline-flex items-center gap-2 cursor-pointer mt-1">
                <input type="checkbox" checked={!!form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="tile-form-visible" className="w-4 h-4 accent-[#7A1F2B]"/>
                <span className="text-sm text-[#2D2424]">Visible on site</span>
              </label>
            </div>
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button type="button" onClick={cancel} className="px-4 py-2 border border-[#E5DBD8] rounded text-sm hover:bg-[#F2E8E4]">Cancel</button>
              <button type="submit" data-testid="tile-form-submit" className="btn-primary px-5 py-2 text-sm">{editing === "new" ? "Create tile" : "Save changes"}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
